import { useState } from "react";
import { Mail, Check } from "lucide-react";
import { API_BASE_URL } from "../lib/env";
import { TurnstileWidget } from "./TurnstileWidget";
import { isCaptchaRejection } from "../lib/captcha-rejection";

export function NewsletterForm({
  className = "",
  testSiteKey,
}: {
  className?: string;
  /** Test seam, forwarded to TurnstileWidget. See its testSiteKey doc. */
  testSiteKey?: string;
}) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [captchaToken, setCaptchaToken] = useState("");
  // Bumped when the API rejects the token, to force a fresh challenge.
  const [captchaReset, setCaptchaReset] = useState(0);
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "err">("idle");
  const [errKind, setErrKind] = useState<"captcha" | "submit" | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "sending") return;

    // NO CLIENT-SIDE SITEVERIFY. The API is the single redeemer.
    //
    // This used to call verifyTurnstileViaWorker(captchaToken) and then POST
    // THE SAME token to /v1/marketing/*. Turnstile tokens are single-use, so
    // the API's redemption would have returned timeout-or-duplicate the moment
    // TURNSTILE_ENFORCEMENT_ENABLED was set to 1 — every genuine submission
    // rejected, and only once that flag was on, which is why it never fired.
    // Same bug LoginPage.tsx already fixed; it had not reached the landing app.
    //
    // Verifying server-side is also the stronger gate: a bot can POST the API
    // directly and skip any browser-side check.

    setStatus("sending");
    setErrKind(null);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/marketing/newsletter`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          website,
          source: "footer",
          captcha_token: captchaToken,
        }),
      });
      if (!res.ok) {
        // A rejected token is SPENT. Clear it and make the widget mint a new
        // one, or every retry resends the dead token and fails again for a
        // reason the user cannot see.
        if (await isCaptchaRejection(res)) {
          setCaptchaToken("");
          setCaptchaReset((n) => n + 1);
          setErrKind("captcha");
          setStatus("err");
          return;
        }
        throw new Error(`status_${res.status}`);
      }
      setStatus("ok");
      setEmail("");
      setCaptchaToken("");
      setErrKind(null);
    } catch {
      setErrKind("submit");
      setStatus("err");
    }
  }

  if (status === "ok") {
    return (
      <p className={`text-sm text-forest-400 inline-flex items-center gap-2 ${className}`}>
        <Check className="h-4 w-4" /> Thanks — we’ll be in touch.
      </p>
    );
  }

  return (
    <form method="post" onSubmit={onSubmit} className={`flex flex-col gap-2 ${className}`} noValidate><div className="flex gap-2">
      {/* Honeypot — bots fill, humans don't see */}
      <label className="sr-only" aria-hidden="true">
        Website
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          style={{
            display: "none",
            position: "absolute",
            left: "-9999px",
          }}
        />
      </label>
      <label htmlFor="newsletter-email" className="sr-only">
        Email address
      </label>
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" aria-hidden />
        <input
          id="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@facility.com"
          autoComplete="email"
          className="w-full rounded-lg bg-ink-800 border border-ink-700 pl-9 pr-3 py-2 text-sm text-white placeholder:text-ink-400 focus:border-forest-500"
        />
      </div>
      <button
        type="submit"
        disabled={status === "sending"}
        className="rounded-lg bg-forest-700 hover:bg-forest-600 text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
      >
        {status === "sending" ? "..." : "Subscribe"}
      </button>
      </div>
      <TurnstileWidget
        onToken={setCaptchaToken}
        theme="dark"
        resetSignal={captchaReset}
        testSiteKey={testSiteKey}
      />
      {status === "err" ? (
        <p role="alert" className="text-xs text-red-400">
          {errKind === "captcha"
            ? "Verification didn't complete. Wait for the widget, then try again."
            : "Something went wrong. Please try again."}
        </p>
      ) : null}
    </form>
  );
}
